import React, { useState, useEffect } from "react";
import { socket } from "../socket";
import { PlayerAvatar, PlayerTag } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { useGameTimer } from "../gameTimer";

// Undercover : chacun a un mot secret, les undercover un mot voisin sans le
// savoir, Mister White aucun. Un mot chacun à tour de rôle, puis on vote.
//
// Le serveur n'envoie à chacun que son propre mot ; les rôles ne se dévoilent
// qu'à l'élimination et à la fin de la manche.
function UndercoverGame({ gameId, player }) {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [draft, setDraft] = useState("");
  const [refused, setRefused] = useState("");
  const { playPop } = useSounds();
  const myId = player?.playerId;

  useEffect(() => {
    socket.on("ucState", setState);
    socket.on("ucWordRefused", ({ error }) => setRefused(error));
    socket.emit("ucGetState", { gameId });
    return () => ["ucState", "ucWordRefused"].forEach(e => socket.off(e));
  }, [gameId]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);

  // Chaque prise de parole repart d'un champ vide
  const turnKey = state ? `${state.manche}-${state.tour}-${state.phase}-${state.speakerId}` : "";
  useEffect(() => { setDraft(""); setRefused(""); }, [turnKey]);
  useEffect(() => { if (state?.phase === "reveal") playPop(); }, [state?.phase, playPop]);

  const phase = state?.phase;
  const left = state?.endsAt ? Math.max(0, Math.ceil((state.endsAt - now) / 1000)) : 0;
  const myTurn = phase === "words" && state?.speakerId === myId;
  const iGuessWhite = phase === "white" && state?.whiteId === myId;
  useGameTimer(state ? { remaining: left, duration: state.duration, idle: phase === "reveal" || phase === "outcome" } : null);
  useCountdownTick(left, { active: myTurn || iGuessWhite });

  if (!state) return <div className="Undercover"><p className="pi-waiting">{t("undercover.waiting")}</p></div>;

  const byId = id => state.players.find(p => p.playerId === id);
  const name = id => byId(id)?.pseudo || "?";
  const roleLabel = role => t(`undercover.role.${role}`);
  const canVote = state.inManche && state.alive;

  const send = (e, event) => {
    e.preventDefault();
    if (!draft.trim()) return;
    socket.emit(event, { gameId, text: draft });
    if (event === "ucWhiteGuess") setDraft("");
  };

  // ── Zone centrale, selon la phase ─────────────────────────────────────────
  let action = null;
  if (phase === "words") {
    action = myTurn ? (
      <>
        <p className="uc-prompt">{t("undercover.yourTurn")}</p>
        <form className="bt-input-row" onSubmit={e => send(e, "ucWord")}>
          <input value={draft} maxLength={30} autoFocus autoComplete="off"
            placeholder={t("undercover.wordPlaceholder")}
            onChange={e => { setDraft(e.target.value); setRefused(""); }} />
          <button className="btn btn-primary" disabled={!draft.trim()}>{t("undercover.send")}</button>
        </form>
        {refused && <p className="uc-error">{refused}</p>}
      </>
    ) : <p className="uc-prompt">{t("undercover.speaking", { pseudo: name(state.speakerId) })}</p>;
  } else if (phase === "continue") {
    action = (
      <>
        <p className="uc-prompt">{t("undercover.continueQuestion")}</p>
        {canVote ? (
          <div className="uc-choices">
            {[true, false].map(again => (
              <button key={String(again)} className={`pi-choice${state.myContinue === again ? " on" : ""}`}
                onClick={() => socket.emit("ucContinue", { gameId, again })}>
                {t(again ? "undercover.again" : "undercover.toVote")}
              </button>
            ))}
          </div>
        ) : <p className="uc-hint">{t("undercover.watching")}</p>}
      </>
    );
  } else if (phase === "vote") {
    action = (
      <>
        <p className="uc-prompt">{t(state.endMode === "accusation" ? "undercover.voteAccuse" : "undercover.voteEliminate")}</p>
        {canVote ? (
          <div className="uc-choices">
            {state.players.filter(p => p.alive && p.playerId !== myId).map(p => (
              <button key={p.playerId} className={`pi-choice uc-target${state.myVote === p.playerId ? " on" : ""}`}
                onClick={() => socket.emit("ucVote", { gameId, targetId: p.playerId })}>
                <PlayerAvatar player={p} size={26} /> {p.pseudo}
              </button>
            ))}
          </div>
        ) : <p className="uc-hint">{t("undercover.watching")}</p>}
      </>
    );
  } else if (phase === "white") {
    action = iGuessWhite ? (
      <>
        <p className="uc-prompt">{t("undercover.whiteCaught")}</p>
        <form className="bt-input-row" onSubmit={e => send(e, "ucWhiteGuess")}>
          <input value={draft} maxLength={30} autoFocus autoComplete="off"
            placeholder={t("undercover.whitePlaceholder")} onChange={e => setDraft(e.target.value)} />
          <button className="btn btn-primary" disabled={!draft.trim()}>{t("undercover.send")}</button>
        </form>
      </>
    ) : <p className="uc-prompt">{t("undercover.whiteGuessing", { pseudo: name(state.whiteId) })}</p>;
  } else if (phase === "outcome" && state.lastResult) {
    const r = state.lastResult;
    action = (
      <p className="uc-prompt">
        {r.tie ? t("undercover.tie")
          : t("undercover.eliminated", { pseudo: name(r.targetId), role: roleLabel(r.role) })}
        {r.whiteGuess !== undefined && (
          <span className="uc-hint"> {t("undercover.whiteMissed", { word: r.whiteGuess || "—" })}</span>
        )}
      </p>
    );
  } else if (phase === "reveal" && state.reveal) {
    const r = state.reveal;
    action = (
      <div className="uc-reveal">
        <p className="uc-winner">{t(`undercover.win.${r.winner}`)}</p>
        <p className="uc-words">
          {t("undercover.civilWord")} <b>{r.civilWord}</b> · {t("undercover.ucWord")} <b>{r.ucWord}</b>
        </p>
      </div>
    );
  }

  return (
    <div className="Undercover">
      <div className="uc-layout">
        <aside className="bt-panel bt-left uc-players">
          <ul className="bt-ranking">
            {[...state.players].sort((a, b) => b.score - a.score).map((p, i) => {
              const words = state.words[p.playerId] || [];
              const gain = state.reveal?.gains?.[p.playerId];
              return (
                <li key={p.playerId} className={`uc-player${p.inManche && !p.alive && phase !== "reveal" ? " out" : ""}`}>
                  <PlayerTag player={p} pos={i + 1} score={p.score}
                    afterName={p.role ? <span className={`uc-role ${p.role}`}>{roleLabel(p.role)}</span> : null}
                    className={`${p.playerId === myId ? "me" : ""}${p.offline ? " offline" : ""}${p.playerId === state.speakerId ? " uc-speaking" : ""}${p.voted ? " uc-voted" : ""}`} />
                  {(words.length > 0 || gain) && (
                    <div className="uc-said">
                      {words.map((w, j) => <span key={j} className={w ? "" : "pass"}>{w || "…"}</span>)}
                      {gain ? <b className="uc-gain">+{gain}</b> : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        <main className="uc-main">
          <p className="hm-kicker">
            {t("undercover.mancheOf", { n: state.manche, total: state.totalManches })}
            <small>{state.maxTours
              ? t("undercover.tourOf", { n: state.tour, total: state.maxTours })
              : t("undercover.tour", { n: state.tour })}</small>
          </p>
          <div className={`uc-secret${state.iAmWhite ? " white" : ""}`}>
            {!state.inManche ? <span className="uc-secret-label">{t("undercover.spectator")}</span>
              : state.iAmWhite ? <>
                  <span className="uc-secret-label">{t("undercover.youAreWhite")}</span>
                  <span className="uc-secret-hint">{t("undercover.whiteHint")}</span>
                </>
              : <>
                  <span className="uc-secret-label">{t("undercover.yourWord")}</span>
                  <span className="uc-secret-word">{state.myWord}</span>
                </>}
          </div>
          {action}
        </main>
      </div>
    </div>
  );
}

export default UndercoverGame;
